import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PermissionService } from './permission.service';
import { AuthService } from './auth.service';

/**
 * Route guard factory that requires specific permissions to access a route.
 * Redirects to appropriate auth pages based on permission state.
 *
 * Redirect behavior:
 * - No permissions at all → /auth/pending (user awaits admin approval)
 * - Permission load error → /auth/error (retry available)
 * - Missing required permissions → /auth/denied (access denied)
 * - Has all permissions → Allow route activation
 *
 * @param permissions - One or more permission strings (requires ALL)
 * @returns CanActivateFn guard function
 *
 * @example
 * ```typescript
 * // Single permission
 * {
 *   path: 'plans',
 *   component: PlansComponent,
 *   canActivate: [authGuard, requirePermission('plans:read')]
 * }
 *
 * // Multiple permissions (requires ALL)
 * {
 *   path: 'plans/edit',
 *   component: PlanEditComponent,
 *   canActivate: [authGuard, requirePermission('plans:read', 'plans:update')]
 * }
 * ```
 */
export function requirePermission(...permissions: string[]): CanActivateFn {
  return async (route, state) => {
    const permissionService = inject(PermissionService);
    const authService = inject(AuthService);
    const router = inject(Router);

    // If not authenticated, redirect to login
    if (!authService.isAuthenticated()) {
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Wait for permissions to be loaded
    if (!permissionService.isLoaded()) {
      await firstValueFrom(toObservable(permissionService.isLoaded).pipe(filter(loaded => loaded)));
    }

    // Check for permission load error
    if (permissionService.hasError()) {
      return router.createUrlTree(['/auth/error'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Check if user has no permissions (pending approval)
    if (permissionService.hasNoPermissions()) {
      return router.createUrlTree(['/auth/pending'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Check if user has all required permissions
    if (!permissionService.hasAllPermissions(permissions)) {
      return router.createUrlTree(['/auth/denied'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // User has all required permissions - allow route activation
    return true;
  };
}

/**
 * Route guard factory that requires any of the specified permissions to access a route.
 * Redirects to appropriate auth pages based on permission state.
 *
 * Redirect behavior:
 * - No permissions at all → /auth/pending (user awaits admin approval)
 * - Permission load error → /auth/error (retry available)
 * - No matching permissions → /auth/denied (access denied)
 * - Has at least one permission → Allow route activation
 *
 * @param permissions - One or more permission strings (requires ANY)
 * @returns CanActivateFn guard function
 *
 * @example
 * ```typescript
 * // User can access if they have ANY of these permissions
 * {
 *   path: 'reports',
 *   component: ReportsComponent,
 *   canActivate: [authGuard, requireAnyPermission('plans:read', 'employees:read')]
 * }
 * ```
 */
export function requireAnyPermission(...permissions: string[]): CanActivateFn {
  return async (route, state) => {
    const permissionService = inject(PermissionService);
    const authService = inject(AuthService);
    const router = inject(Router);

    // If not authenticated, redirect to login
    if (!authService.isAuthenticated()) {
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Wait for permissions to be loaded
    if (!permissionService.isLoaded()) {
      await firstValueFrom(toObservable(permissionService.isLoaded).pipe(filter(loaded => loaded)));
    }

    // Check for permission load error
    if (permissionService.hasError()) {
      return router.createUrlTree(['/auth/error'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Check if user has no permissions (pending approval)
    if (permissionService.hasNoPermissions()) {
      return router.createUrlTree(['/auth/pending'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Check if user has any of the required permissions
    if (!permissionService.hasAnyPermission(permissions)) {
      return router.createUrlTree(['/auth/denied'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // User has at least one required permission - allow route activation
    return true;
  };
}

/**
 * Route guard factory that requires specific roles to access a route.
 * Redirects to appropriate auth pages based on role state.
 *
 * Redirect behavior:
 * - No permissions at all → /auth/pending (user awaits admin approval)
 * - Permission load error → /auth/error (retry available)
 * - Missing required roles → /auth/denied (access denied)
 * - Has all roles → Allow route activation
 *
 * @param roles - One or more role names (requires ALL)
 * @returns CanActivateFn guard function
 *
 * @example
 * ```typescript
 * // Single role
 * {
 *   path: 'admin',
 *   component: AdminComponent,
 *   canActivate: [authGuard, requireRole('admin')]
 * }
 *
 * // Multiple roles (requires ALL)
 * {
 *   path: 'super-admin',
 *   component: SuperAdminComponent,
 *   canActivate: [authGuard, requireRole('admin', 'super')]
 * }
 * ```
 */
export function requireRole(...roles: string[]): CanActivateFn {
  return async (route, state) => {
    const permissionService = inject(PermissionService);
    const authService = inject(AuthService);
    const router = inject(Router);

    // If not authenticated, redirect to login
    if (!authService.isAuthenticated()) {
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Wait for permissions to be loaded
    if (!permissionService.isLoaded()) {
      await firstValueFrom(toObservable(permissionService.isLoaded).pipe(filter(loaded => loaded)));
    }

    // Check for permission load error
    if (permissionService.hasError()) {
      return router.createUrlTree(['/auth/error'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Check if user has no permissions (pending approval)
    if (permissionService.hasNoPermissions()) {
      return router.createUrlTree(['/auth/pending'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Check if user has all required roles
    if (!permissionService.hasAllRoles(roles)) {
      return router.createUrlTree(['/auth/denied'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // User has all required roles - allow route activation
    return true;
  };
}

/**
 * Route guard factory that requires any of the specified roles to access a route.
 * Redirects to appropriate auth pages based on role state.
 *
 * Redirect behavior:
 * - No permissions at all → /auth/pending (user awaits admin approval)
 * - Permission load error → /auth/error (retry available)
 * - No matching roles → /auth/denied (access denied)
 * - Has at least one role → Allow route activation
 *
 * @param roles - One or more role names (requires ANY)
 * @returns CanActivateFn guard function
 *
 * @example
 * ```typescript
 * // User can access if they have ANY of these roles
 * {
 *   path: 'management',
 *   component: ManagementComponent,
 *   canActivate: [authGuard, requireAnyRole('admin', 'manager')]
 * }
 * ```
 */
export function requireAnyRole(...roles: string[]): CanActivateFn {
  return async (route, state) => {
    const permissionService = inject(PermissionService);
    const authService = inject(AuthService);
    const router = inject(Router);

    // If not authenticated, redirect to login
    if (!authService.isAuthenticated()) {
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Wait for permissions to be loaded
    if (!permissionService.isLoaded()) {
      await firstValueFrom(toObservable(permissionService.isLoaded).pipe(filter(loaded => loaded)));
    }

    // Check for permission load error
    if (permissionService.hasError()) {
      return router.createUrlTree(['/auth/error'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Check if user has no permissions (pending approval)
    if (permissionService.hasNoPermissions()) {
      return router.createUrlTree(['/auth/pending'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Check if user has any of the required roles
    if (!permissionService.hasAnyRole(roles)) {
      return router.createUrlTree(['/auth/denied'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // User has at least one required role - allow route activation
    return true;
  };
}

/**
 * Guard for the /auth/denied page that ensures users are on the correct
 * access page based on their current permission state.
 *
 * This guard prevents stale states when a user reloads the /auth/denied page
 * after their permission state has changed (e.g., admin removed all roles).
 *
 * Redirect behavior:
 * - Permission load error → /auth/error
 * - No permissions at all → /auth/pending
 * - User has some permissions → Allow access to /auth/denied
 *
 * The guard preserves the `returnUrl` query parameter when redirecting,
 * allowing the denied-access component to redirect back when permissions change.
 *
 * @returns CanActivateFn guard function
 */
export const deniedPageGuard: CanActivateFn = async route => {
  const permissionService = inject(PermissionService);
  const authService = inject(AuthService);
  const router = inject(Router);

  // Preserve returnUrl from query params for redirect scenarios
  const returnUrl = route.queryParams['returnUrl'];

  // If not authenticated, redirect to login (preserve returnUrl)
  if (!authService.isAuthenticated()) {
    return router.createUrlTree(['/login'], {
      queryParams: returnUrl ? { returnUrl } : undefined,
    });
  }

  // Wait for permissions to be loaded
  if (!permissionService.isLoaded()) {
    await firstValueFrom(toObservable(permissionService.isLoaded).pipe(filter(loaded => loaded)));
  }

  // Check for permission load error - redirect to error page
  if (permissionService.hasError()) {
    return router.createUrlTree(['/auth/error'], {
      queryParams: returnUrl ? { returnUrl } : undefined,
    });
  }

  // Check if user has no permissions - they should be on pending page instead
  if (permissionService.hasNoPermissions()) {
    return router.createUrlTree(['/auth/pending'], {
      queryParams: returnUrl ? { returnUrl } : undefined,
    });
  }

  // User has some permissions but was denied access to a specific route
  // Allow them to stay on the denied page
  return true;
};
